/**
 * Client-side date formatting utilities
 * These functions are safe to use in client components
 */

/**
 * Format a date without timezone conversion (for dates already in correct timezone)
 * @param dateString - The date string to format
 * @param options - Formatting options
 * @returns Formatted date string or 'Invalid Date' if error
 */
export function formatDateSimple(
  dateString: any, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
): string {
  try {
    if (!dateString) return 'Invalid Date'
    
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
    
    return date.toLocaleDateString('en-IN', options)
  } catch (error) {
    return 'Invalid Date'
  }
}

/**
 * Format a date for IST timezone with proper error handling
 * @param dateString - The date string to format
 * @param options - Formatting options
 * @returns Formatted date string in IST or 'Invalid Date' if error
 */
export function formatDateIST(
  dateString: any, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  }
): string {
  try {
    if (!dateString) return 'Invalid Date'
    
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
    
    return date.toLocaleString('en-IN', options)
  } catch (error) {
    return 'Invalid Date'
  }
}

/**
 * Format a date for display in tables (compact format) in IST
 */
export function formatTableDateIST(dateString: any): string {
  return formatDateIST(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  })
}

/**
 * Format a date for detailed display (full format) in IST
 */
export function formatDetailedDateIST(dateString: any): string {
  return formatDateIST(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  })
}

/**
 * Convert UTC date to IST (Indian Standard Time)
 * @param dateString - The date string (usually UTC from database)
 * @returns Date object in IST
 */
export function convertToIST(dateString: string | Date): Date {
  const date = new Date(dateString)
  // IST is UTC+5:30
  const istOffset = 5.5 * 60 * 60 * 1000 // 5.5 hours in milliseconds
  return new Date(date.getTime() + istOffset)
}

/**
 * Get current date in IST
 * @returns Current date in IST
 */
export function getCurrentDateIST(): Date {
  return convertToIST(new Date())
}

/**
 * Format a database date by treating it as local time (no timezone conversion)
 * @param dateString - The date string from database (e.g., "2025-06-26 22:31:55.632")
 * @param options - Formatting options
 * @returns Formatted date string or 'Invalid Date' if error
 */
export function formatDatabaseDate(
  dateString: any, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
): string {
  try {
    if (!dateString) return 'Invalid Date'
    
    let date: Date
    
    // If it's already a Date object, use it directly
    if (dateString instanceof Date) {
      date = dateString
    } else if (typeof dateString === 'string') {
      // Treat the database date as local time (not UTC)
      // Remove any timezone indicators and parse as local
      const cleanString = dateString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '')
      date = new Date(cleanString)
    } else {
      date = new Date(dateString)
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
    
    // Format without timezone conversion
    return date.toLocaleDateString('en-IN', options)
  } catch (error) {
    return 'Invalid Date'
  }
} 